// TODO :: https://router.vuejs.org/guide/migration/index.html#removed-star-or-catch-all-routes
import { createRouter, createWebHistory } from 'vue-router'
import { routeAuthorization } from './helpers';

// Import Routes
import AppRoutes from './app-routes';
import PublicRoutes from './public-routes';
import AuthRoutes from '@/views/auth/routes';

// Import Base Router Config Options
import { CONFIG_ROUTES } from '@/config/routes';

const router = createRouter({
  history: createWebHistory(),
  linkActiveClass: 'active',
  linkExactActiveClass: 'active-exact',
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash
      };
    }
    return { left: 0, top: 0 };
  },
  routes: [
    /**
     * Do not change the order of the routes below. It's
     * needed to maintain specificity of the routes.
     */
    ...AppRoutes,
    ...AuthRoutes,
    ...PublicRoutes,
    {
      path: '/shadow-zone',
      name: 'shadow-zone',
      component: () => import('./ShadowZone.vue')
    },

    /**
     * Wildcard Redirect. Should the User find themselves at
     * a URL that exists outside the routes above they will
     * be redirected to the home page.
     */
    {
      path: '/:pathMatch(.*)*',
      redirect: { name: CONFIG_ROUTES.APP_HOME }
    }
  ]
});

router.beforeEach(routeAuthorization);

/**
 * A component is "already resolved" when it is a component definition rather
 * than a lazy `() => import(...)` loader. Mirrors Vue Router's own check —
 * functional components are functions too, so `typeof x === 'function'` alone
 * is not enough to identify a loader.
 */
const isResolvedRouteComponent = (component: unknown): boolean => {
  if (typeof component === 'object') return true;
  if (typeof component !== 'function') return true;
  return 'displayName' in component || 'props' in component || '__vccOpts' in component;
};

/**
 * Load lazy route components one at a time, before Vue Router loads them.
 *
 * WebKit (reproduced on Safari 18.6) mis-links shared modules when several
 * lazy route components are imported concurrently. Vue Router's
 * `resolveAsyncComponents` imports every matched record's component at once,
 * and where those module graphs overlap Safari can start evaluating one module
 * before a dependency it imports has finished. The imported binding is still
 * `undefined`, the module throws, the navigation aborts — and Vue Router
 * swallows that rejection in production builds, leaving the User stuck on the
 * boot overlay with no error.
 *
 * `/qx/live` is `APP_HOME_REDIRECT`, so every login lands on the one route
 * that matches three records (AppContainer + LiveContainer + LivePage) whose
 * graphs all pull in `@/composables/use-search` — which is why this presented
 * as "cannot log in on Safari".
 *
 * Awaiting the loaders sequentially puts each module in the registry before
 * Vue Router's concurrent pass runs, making that pass a no-op. Measured over
 * 68 interleaved trials on Safari 18.6: concurrent 0/34 succeeded,
 * sequential 34/34.
 *
 * DO NOT "optimise" this into a `Promise.all` — the concurrency is the bug.
 *
 * This MUST be a `beforeEach` guard, not `beforeResolve`. Vue Router loads the
 * async components while collecting `beforeRouteEnter` guards, which happens
 * *before* `beforeResolve` runs — a `beforeResolve` hook fires too late to
 * warm anything. It is registered after `routeAuthorization` so we only pay
 * the cost for navigations that survive the auth guards.
 */
router.beforeEach(async (to) => {
  for (const record of to.matched) {
    for (const component of Object.values(record.components ?? {})) {
      if (isResolvedRouteComponent(component)) continue;
      await (component as unknown as () => Promise<unknown>)();
    }
  }
});

/**
 * Surface navigation failures.
 *
 * Vue Router only warns about a failed navigation in development — in a
 * production build it swallows the rejection entirely. That is why the Safari
 * module-loading bug above was invisible for so long: the navigation died, the
 * route never resolved, and the User sat on the boot overlay with a clean
 * console. Anything that breaks a navigation should be loud.
 */
router.onError((error, to, from) => {
  console.error('Router navigation failed.', {
    error,
    to: to?.fullPath,
    from: from?.fullPath,
  });
});

export default router
